import { TrustiiApiClient } from "../api/client.js";
import { incrementGCounter } from "../crdt/gCounter.js";
import { stampRegister } from "../crdt/lwwRegister.js";
import { CrdtState } from "../crdt/merge.js";
import { WalletSyncEngine } from "../sync/WalletSyncEngine.js";

export interface ServerPersona {
  id?: number;
  slug: string;
  name: string;
  persona_type?: string;
  display_name?: string | null;
  is_default?: boolean;
  active?: boolean;
}

interface PersonasResponse {
  personas: ServerPersona[];
}

interface ActivePersonaResponse {
  success?: boolean;
  active_persona?: ServerPersona;
}

export async function hydrateCrdtFromServer(
  api: TrustiiApiClient,
  engine: WalletSyncEngine,
): Promise<boolean> {
  const state = engine.getLocalState();
  if (Object.keys(state.personas).length > 0) {
    return false;
  }

  const response = (await api.getPersonas()) as unknown as PersonasResponse;
  const personas = response.personas ?? [];
  if (personas.length === 0) {
    return false;
  }

  engine.localMutate((crdt) => {
    const deviceId = engine.getDeviceKeys()!.syncDeviceId;
    const counter = incrementGCounter(crdt.clock, deviceId);
    for (const persona of personas) {
      crdt.personas[persona.slug] = {
        slug: stampRegister(persona.slug, counter, deviceId),
        name: stampRegister(persona.name, counter, deviceId),
        display_name: stampRegister(persona.display_name ?? persona.name, counter, deviceId),
        persona_type: stampRegister(persona.persona_type ?? "personal", counter, deviceId),
        is_default: stampRegister(Boolean(persona.is_default), counter, deviceId),
        active: stampRegister(true, counter, deviceId),
      };
    }
  });

  return true;
}

export async function syncPersonasToServer(
  api: TrustiiApiClient,
  crdtState: CrdtState,
  serverPersonas?: ServerPersona[],
): Promise<void> {
  const remote = serverPersonas ?? ((await api.getPersonas()) as unknown as PersonasResponse).personas ?? [];
  const remoteBySlug = new Map(remote.map((persona) => [persona.slug, persona]));

  for (const [slug, entry] of Object.entries(crdtState.personas)) {
    const activeField = entry.active;
    if (activeField?.value === false) {
      continue;
    }

    const name = String(entry.name?.value ?? slug);
    const displayName = entry.display_name?.value != null
      ? String(entry.display_name.value)
      : name;
    const personaType = String(entry.persona_type?.value ?? "personal");
    const isDefault = Boolean(entry.is_default?.value);
    const remotePersona = remoteBySlug.get(slug);

    if (!remotePersona) {
      await api.createPersona({
        slug,
        name,
        display_name: displayName,
        persona_type: personaType,
        is_default: isDefault,
      });
      continue;
    }

    const updates: Record<string, unknown> = {};
    if (remotePersona.name !== name) updates.name = name;
    if ((remotePersona.display_name ?? remotePersona.name) !== displayName) {
      updates.display_name = displayName;
    }
    if ((remotePersona.persona_type ?? "personal") !== personaType) {
      updates.persona_type = personaType;
    }
    if (Boolean(remotePersona.is_default) !== isDefault) {
      updates.is_default = isDefault;
    }

    if (Object.keys(updates).length > 0) {
      await api.updatePersona(slug, updates);
    }
  }

  const activeSlug = readActivePersonaSlug(crdtState);
  if (activeSlug) {
    const walletProfile = await api.getMe();
    const currentActive = (walletProfile as { default_persona?: { slug?: string } }).default_persona?.slug;
    if (currentActive !== activeSlug) {
      await api.setActivePersona(activeSlug);
    }
  }
}

function readActivePersonaSlug(state: CrdtState): string | null {
  for (const [slug, entry] of Object.entries(state.personas)) {
    if (entry.active?.value === true) {
      return slug;
    }
  }
  for (const [slug, entry] of Object.entries(state.personas)) {
    if (entry.is_default?.value === true) {
      return slug;
    }
  }
  const slugs = Object.keys(state.personas);
  return slugs[0] ?? null;
}

export async function afterSuccessfulPush(
  api: TrustiiApiClient,
  engine: WalletSyncEngine,
  personasChanged: boolean,
): Promise<void> {
  if (!personasChanged) {
    return;
  }
  await syncPersonasToServer(api, engine.getLocalState());
}
